import type { InspectionEvent } from 'xstate';

/*
 * Lightweight transition observability for the upload actor system.
 *
 * A bounded ring buffer records every state CHANGE (context-only updates,
 * e.g. progress ticks, are skipped) across the session and all spawned
 * children. The controller forwards each breadcrumb to Bugsnag via
 * leaveBreadcrumb, so any later notify() carries the event trail that led
 * to a stuck or failed upload. Deliberately observe-only: no watchdogs, no
 * self-healing.
 */

export type UploadTransitionBreadcrumb = {
	actorId: string;
	eventType: string;
	state: string;
	ts: number;
};

export type UploadInspector = {
	inspect: (inspectionEvent: InspectionEvent) => void;
	getBreadcrumbs: () => UploadTransitionBreadcrumb[];
};

export type UploadInspectorOptions = {
	bufferSize?: number;
	now?: () => number;
	onBreadcrumb?: (breadcrumb: UploadTransitionBreadcrumb) => void;
};

const defaultBufferSize = 200;

function hasStateValue(snapshot: unknown): snapshot is { value: unknown } {
	return (
		typeof snapshot === 'object'
		&& snapshot !== null
		&& 'value' in snapshot
	);
}

/*
 * Inspection events expose actors as ActorRefLike (sessionId only); real
 * actor refs additionally carry the human-readable id. Prefer it when
 * present.
 */
function readActorId(actorRef: { sessionId: string }): string {
	if ('id' in actorRef) {
		const { id } = actorRef;

		if (typeof id === 'string') {
			return id;
		}
	}

	return actorRef.sessionId;
}

export function createUploadInspector(options: UploadInspectorOptions = {}): UploadInspector {
	const bufferSize = options.bufferSize ?? defaultBufferSize;

	if (!Number.isInteger(bufferSize) || bufferSize <= 0) {
		throw new Error('Upload inspector bufferSize must be a positive integer');
	}

	const now = options.now ?? Date.now;
	const breadcrumbs: UploadTransitionBreadcrumb[] = [];
	const lastStateByActor = new Map<string, string>();

	return {
		inspect: (inspectionEvent) => {
			if (inspectionEvent.type !== '@xstate.snapshot') {
				return;
			}

			const { snapshot } = inspectionEvent;

			// Promise/callback actors carry no state value; only machine
			// snapshots contribute transition breadcrumbs.
			if (!hasStateValue(snapshot) || snapshot.value === undefined) {
				return;
			}

			const state = JSON.stringify(snapshot.value);
			const actorId = readActorId(inspectionEvent.actorRef);

			if (lastStateByActor.get(actorId) === state) {
				return;
			}

			lastStateByActor.set(
				actorId,
				state,
			);

			const breadcrumb: UploadTransitionBreadcrumb = {
				actorId,
				eventType: inspectionEvent.event.type,
				state,
				ts: now(),
			};

			breadcrumbs.push(breadcrumb);

			if (breadcrumbs.length > bufferSize) {
				breadcrumbs.splice(
					0,
					breadcrumbs.length - bufferSize,
				);

				/*
				 * Rebuild the per-actor dedup map from the retained
				 * breadcrumbs so it stays bounded by the ring buffer instead
				 * of growing for every actor id ever seen. Later breadcrumbs
				 * overwrite earlier ones, leaving each actor's most recent
				 * in-buffer state for continued dedup.
				 */
				lastStateByActor.clear();

				for (const retained of breadcrumbs) {
					lastStateByActor.set(
						retained.actorId,
						retained.state,
					);
				}
			}

			options.onBreadcrumb?.(breadcrumb);
		},
		getBreadcrumbs: () => [...breadcrumbs],
	};
}
